import type { MetricsBreakdownRow } from '@dyspatch/core';
import { useState } from 'react';
import { Panel, PanelHead } from '@/components/ui/panel';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useSort } from '@/hooks/use-sort';
import { breakdownColumns, breakdownSortOptions, sortBreakdownRows } from '@/lib/metrics-table';
import { cn } from '@/lib/utils';

/**
 * The window cut by project, and the same window cut by person.
 *
 * This replaces the two flat tallies the page used to end on, which were a
 * key and a count with no columns and no order a reader could choose. The
 * figures were never the problem; not being able to ask "which project is
 * costing the most" of them was.
 *
 * One component, used twice, because the two tables are the same shape: core
 * builds `byRepo` and `byPerson` from one `breakdownRows`, so the only thing
 * that differs here is what the key column is called and which filter a row
 * click applies.
 *
 * Both instances read and write the one `sort`/`dir` pair in the URL, so the
 * two tables are always sorted the same way. That is deliberate rather than an
 * oversight: the columns are identical, the question a reader is asking
 * ("sorted by cost") is about the window and not about one table, and it keeps
 * a pasted link short. A second namespaced pair would be the change if the
 * columns ever diverge.
 */

const COLLAPSED_ROWS = 5;

export function BreakdownTable({
  title,
  keyLabel,
  rows,
  empty,
  active,
  onPick,
}: {
  title: string;
  /** What the first column is called, and what a row is: a project or a person. */
  keyLabel: string;
  rows: readonly MetricsBreakdownRow[];
  /** One line when the window produced no rows at all. */
  empty: string;
  /** The key this dimension is already filtered to, so the row can say so. */
  active: string | null;
  /** Narrow the page to this row. Called with null when the active row is clicked again. */
  onPick: (key: string | null) => void;
}) {
  const columns = breakdownColumns(keyLabel);
  const options = breakdownSortOptions(columns);
  const sorting = useSort(options, (row) => row.key);
  const [expanded, setExpanded] = useState(false);

  const sorted = sortBreakdownRows(rows, options, sorting.state);
  const shown = expanded ? sorted : sorted.slice(0, COLLAPSED_ROWS);

  return (
    <Panel>
      <PanelHead
        title={title}
        meta={rows.length ? `${rows.length} ${rows.length === 1 ? 'row' : 'rows'}` : undefined}
      />
      {sorted.length ? (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((column) => {
                  const chosen = sorting.state.key === column.value;
                  return (
                    <TableHead
                      key={column.value}
                      aria-sort={
                        chosen
                          ? sorting.state.direction === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                      className={cn('p-0', column.numeric && 'text-right')}
                    >
                      {/* The header is the control. Clicking the key already in
                          use reverses it; clicking another opens it at its
                          natural end, which `pickSort` decides. */}
                      <button
                        type="button"
                        onClick={() => sorting.choose(column.value)}
                        className={cn(
                          'hover:text-foreground flex h-[29px] w-full items-center gap-1 px-gutter',
                          'transition-colors duration-100 outline-none focus-visible:text-foreground',
                          column.numeric && 'justify-end',
                          chosen && 'text-foreground',
                        )}
                      >
                        <span className="truncate">{column.label}</span>
                        <span aria-hidden className={cn('text-fg-3', !chosen && 'opacity-0')}>
                          {sorting.state.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      </button>
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((row) => {
                const on = row.key === active;
                return (
                  <TableRow key={row.key} className={cn(on && 'bg-sel')}>
                    {columns.map((column) =>
                      column.value === 'key' ? (
                        <TableCell key={column.value} className="p-0">
                          {/* The way into a slice: the table is how a reader
                              finds the project worth looking at on its own. */}
                          <button
                            type="button"
                            onClick={() => onPick(on ? null : row.key)}
                            aria-pressed={on}
                            className={cn(
                              'hover:text-foreground block w-full truncate px-gutter py-row text-left text-xs',
                              'transition-colors duration-100 outline-none focus-visible:text-foreground',
                              on ? 'text-foreground font-medium' : 'text-fg-2',
                            )}
                            title={on ? `Show every ${keyLabel.toLowerCase()}` : `Only ${row.key}`}
                          >
                            {row.key}
                          </button>
                        </TableCell>
                      ) : (
                        <TableCell
                          key={column.value}
                          className="tnum text-fg-2 text-right text-xs whitespace-nowrap"
                        >
                          {column.cell(row)}
                        </TableCell>
                      ),
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {sorted.length > COLLAPSED_ROWS ? (
            // In place, not a dialog: the rows are already on the page and this
            // only decides how many of them are drawn.
            <button
              type="button"
              onClick={() => setExpanded((previous) => !previous)}
              className="text-fg-3 hover:text-foreground w-full border-t px-gutter py-row text-left text-2xs transition-colors duration-100"
            >
              {expanded ? 'Show fewer' : `Show all ${sorted.length}`}
            </button>
          ) : null}
        </>
      ) : (
        <p className="text-fg-3 px-gutter py-4 text-center text-xs">{empty}</p>
      )}
    </Panel>
  );
}
